import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export type Rol = 'admin' | 'agent';
export type EstadoPersona = 'activo' | 'pendiente' | 'caducada' | 'sin_acceso';

export const NOMBRE_ROL: Record<Rol, string> = { admin: 'Administrador', agent: 'Agente' };

/** Una persona del equipo (Cognito). `username` es su identificador interno. */
export interface Persona {
  username: string;
  email: string;
  nombre?: string;
  rol: Rol | null;
  estado: EstadoPersona;
  cliente_web: boolean;
  soy_yo: boolean;
  desde: string;
}

/** Solo admin. */
export function useEquipo(activo: boolean) {
  return useQuery<Persona[]>({
    queryKey: ['equipo'],
    queryFn: async () => (await api.get('/crm/equipo')).data.items,
    enabled: activo,
  });
}

export function useInvitar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { email: string; nombre: string; rol: Rol }) =>
      (await api.post('/crm/equipo', p)).data as { persona: Persona; ya_tenia_cuenta: boolean },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipo'] }),
  });
}

/** {rol} cambia el rol; {acceso: false} lo quita; {acceso: true} lo devuelve. */
export function useCambiarPersona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ username, ...cambio }: { username: string; rol?: Rol; acceso?: boolean }) =>
      (await api.put(`/crm/equipo/${encodeURIComponent(username)}`, cambio)).data as Persona,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipo'] }),
  });
}

export function useReenviar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (username: string) =>
      (await api.post(`/crm/equipo/${encodeURIComponent(username)}/reenviar`)).data as Persona,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipo'] }),
  });
}

/** El mensaje que manda la API en un 400/409, para enseñarlo tal cual. */
export const mensajeDe = (e: unknown) =>
  (e as { response?: { data?: { message?: string } } } | null)?.response?.data?.message;
